import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ProjectOriginType } from '@aksia/enums';
import {
  ICancelProjectDTO,
  IChangeLogItem,
  ICompleteProjectResponse,
  EmailAddress,
  EntityItem,
  IFileExport,
  IidName,
  ProjectFilter,
  ISearchProjectFilter,
  IServerResponse,
  IOwner,
  Project,
  IProjectData,
  ProjectEntity,
  IProjectNote,
  IProjectRequest,
  IProjectsViewModel,
  ProjectTemplate,
  IRelatedEntity,
  IUpdateProjectDTO,
} from '@aksia/models';
import { ColumnState } from 'ag-grid-community';
import { EntitySearchItemService } from '../entity/entity-search-item.service';
import { CedricApiService } from '../../infrastructure/api/cedric.api.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private projectsUrl;
  private projectEntitiesUrl;
  private documentManagerUrl;
  private api = inject(CedricApiService);
  private entitySearchItemService = inject(EntitySearchItemService);

  emails: WritableSignal<EmailAddress[]> = signal<EmailAddress[]>([]);
  recurrecences: WritableSignal<IidName[]> = signal<IidName[]>([]);
  owners: WritableSignal<IOwner[]> = signal<IOwner[]>([]);
  templates: WritableSignal<ProjectTemplate[]> = signal<ProjectTemplate[]>([]);

  constructor() {
    this.projectsUrl = 'Projects';
    this.projectEntitiesUrl = 'ProjectEntities';
    this.documentManagerUrl = 'ProjectDocumentManager';
  }

  getProjectsViewModel(): Observable<IProjectsViewModel> {
    const url = this.projectsUrl + '/GetProjectsViewModel';
    return this.api.post(url, {}).pipe(
      catchError(this.handleError),
      tap((response) => {
        if (response.error) return;
        this.handleProjectsViewModel(response);
      }),
    );
  }

  private handleError(errorRes: HttpErrorResponse): any {
    throwError(
      () =>
        'Projects were not properly loaded. Please refresh, if this persists contact Support.',
    );
  }
  private handleProjectsViewModel(response: IProjectsViewModel) {
    response.projectTemplatesFilters = response.projectTemplatesFilters || [];
    this.emails.set(response.emails);
    this.recurrecences.set(response.recurrences);
    this.owners.set(
      response.users?.map((u: any) => ({
        ownerId: u.userId,
        ownerName: u.fullName,
        ownerEmail: u.email,
      })) || [],
    );
    this.templates.set(response.projectTemplates);
  }

  isProjectInAksiaReadingList(project: Project): boolean {
    for (const section of project.customFieldDefinition?.customFieldSections ??
      []) {
      for (const dataDef of section.customFieldDataDefinitions ?? []) {
        if (dataDef.systemKey === 'AksiaReadingList') {
          return dataDef.dataValue === 19;
        }
      }
    }
    return false;
  }

  isProjectImportant(project: Project): boolean {
    for (const section of project.customFieldDefinition?.customFieldSections ??
      []) {
      for (const dataDef of section.customFieldDataDefinitions ?? []) {
        if (dataDef.systemKey === 'Important') {
          return dataDef.dataValue !== 12;
        }
      }
    }
    return false;
  }

  getProjectsVMForEvents(): Observable<{
    projectTemplates: ProjectTemplate[];
    projectFilters: ProjectFilter[];
  }> {
    const url = this.projectsUrl + '/GetProjectsVMForEvents';
    return this.api.post(url, {});
  }

  getProjects({
    filters,
  }: {
    filters: ISearchProjectFilter | null;
  }): Observable<IProjectData[]> {
    const url = this.projectsUrl + '/GetProjectsForDashboard';
    return this.api.post(url, { filter: filters });
  }

  getAllProjects(
    filter: ISearchProjectFilter | undefined,
  ): Observable<{ projects: Array<IProjectData> }> {
    const url = this.projectsUrl + '/GetAllProjectsForDashboard';
    return this.api.post(url, { filter });
  }

  getProject(
    projectId: number | string,
  ): Observable<IServerResponse & { project: Project }> {
    const url = this.projectsUrl + '/GetProject';
    return this.api.post(url, { projectId });
  }

  createProject(
    newProject: Partial<Project>,
    type: ProjectOriginType = ProjectOriginType.Project,
    checkExisting: boolean = false,
  ): Observable<any> {
    const url = this.projectsUrl + '/CreateProject';
    return this.api.post(url, { newProject, type, checkExisting });
  }

  searchProjects(query: string): Observable<Array<IProjectData>> {
    const url = this.projectsUrl + '/SearchProjects';
    if (query && query.length > 0) return this.api.post(url, { query });
    return of([]);
  }

  completeProject(
    completeRequest: IProjectRequest,
  ): Observable<{ response: ICompleteProjectResponse }> {
    const url = this.projectsUrl + '/CompleteProject';
    return this.api.post(url, completeRequest);
  }

  republishProject(
    republishRequest: IProjectRequest,
  ): Observable<IServerResponse> {
    const url = this.projectsUrl + '/RepublishProject';
    return this.api.post(url, republishRequest);
  }

  recallProject(projectId: number | string): Observable<IServerResponse> {
    const url = this.projectsUrl + '/RecallProject';
    return this.api.post(url, { projectId });
  }

  updateProject(project: IUpdateProjectDTO): Observable<IServerResponse> {
    const url = this.projectsUrl + '/UpdateProject';
    return this.api.post(url, { project });
  }

  cancelProject(cancelRequest: ICancelProjectDTO): Observable<IServerResponse> {
    const url = this.projectsUrl + '/CancelProject';
    return this.api.post(url, cancelRequest);
  }

  revertCancelProject(projectId: number | string): Observable<IServerResponse> {
    const url = this.projectsUrl + '/RevertCancelProject';
    return this.api.post(url, { projectId });
  }

  getRelatedProjects(relatedProjectsRequest: any): Observable<any> {
    const url = this.projectsUrl + '/GetRelatedProjects';
    return this.api.post(url, relatedProjectsRequest);
  }

  suspend(projectId: number | string): Observable<IServerResponse> {
    const url = this.projectsUrl + '/SuspendProject';
    return this.api.post(url, { projectId });
  }

  deleteProject(projectId: number | string): Observable<IServerResponse> {
    const url = this.projectsUrl + '/DeleteProject';
    return this.api.post(url, { projectId });
  }

  createProjectNote(projectId: number, note: string): Observable<IProjectNote> {
    const url = this.projectsUrl + '/CreateProjectNote';
    return this.api.post(url, { projectId, note });
  }

  updateProjectNote(projectNote: IProjectNote): Observable<IServerResponse> {
    const url = this.projectsUrl + '/UpdateProjectNote';
    return this.api.post(url, { projectNote });
  }

  deleteProjectNote(projectNoteId: number): Observable<IServerResponse> {
    const url = this.projectsUrl + '/DeleteProjectNote';
    return this.api.post(url, { projectNoteId });
  }

  getProjectFilters(): Observable<Array<ProjectFilter>> {
    const url = this.projectsUrl + '/GetProjectFilters';
    return this.api.post(url, {});
  }

  createProjectFilter(
    filter: ISearchProjectFilter | undefined,
  ): Observable<ProjectFilter & IServerResponse> {
    const url = this.projectsUrl + '/CreateProjectFilter';
    return this.api.post(url, { filter });
  }

  updateProjectFilter(
    filter: ISearchProjectFilter | undefined,
  ): Observable<IServerResponse> {
    const url = this.projectsUrl + '/UpdateProjectFilter';
    return this.api.post(url, { filter });
  }

  updateProjectFilterName(
    projectFilterId: number,
    name: string,
  ): Observable<IServerResponse> {
    const url = this.projectsUrl + '/UpdateProjectFilterName';
    return this.api.post(url, { projectFilterId, name });
  }

  toggleFilterDefault(
    projectFilterId: number,
    isDefault: boolean,
  ): Observable<IServerResponse> {
    const url = this.projectsUrl + '/ToggleProjectFilterDefault';
    return this.api.post(url, { projectFilterId, isDefault });
  }

  deleteProjectFilter(id: number): Observable<IServerResponse> {
    const url = this.projectsUrl + '/DeleteProjectFilter';
    return this.api.post(url, { projectFilterId: id });
  }

  createProjectTemplateFromProject({
    projectId,
    name,
  }: {
    projectId: number;
    name: string;
  }): Observable<IServerResponse> {
    const url = this.projectsUrl + '/CreateProjectTemplateFromProject';
    return this.api.post(url, { projectId, name });
  }

  searchEntities(
    input: string,
    entityTypeIds: string | null,
  ): Observable<Array<EntityItem>> {
    const url = this.projectEntitiesUrl + '/SearchEntities';
    return this.api
      .post(url, {
        query: input,
        param: entityTypeIds,
      })
      .pipe(
        map((results) => {
          results.forEach((item: IRelatedEntity) => {
            item['icon'] = this.entitySearchItemService.processIcon(
              item.entityTypeId,
            );
            item['iconColor'] = this.entitySearchItemService.processIconColor(
              item.entityTypeId,
            );
          });
          return results; // Return the modified results
        }),
      ) as Observable<Array<EntityItem>>;
  }

  addEntity(
    newProjectEntity: ProjectEntity,
  ): Observable<ProjectEntity & IServerResponse> {
    const url = this.projectEntitiesUrl + '/CreateProjectEntity';
    return this.api.post(url, { newProjectEntity });
  }

  deleteEntity(projectEntity: ProjectEntity): Observable<IServerResponse> {
    const url = this.projectEntitiesUrl + '/DeleteProjectEntity';
    return this.api.post(url, { projectEntity });
  }

  getRelatedEntities(
    entityId: number,
    entityTypeId: number,
  ): Observable<IRelatedEntity[]> {
    const url = this.projectEntitiesUrl + '/GetRelatedEntities';
    return this.api.post(url, { entityId, entityTypeId });
  }

  replaceEntities(
    replaceEntity: any,
  ): Observable<IServerResponse & { failed: number[] }> {
    const url = this.projectEntitiesUrl + '/ReplaceEntities';
    return this.api.post(url, replaceEntity);
  }

  removeEntities(
    removeEntities: any,
  ): Observable<IServerResponse & { failed: number[] }> {
    const url = this.projectEntitiesUrl + '/RemoveEntities';
    return this.api.post(url, removeEntities);
  }

  getProjectChangeLog(projectId: number): Observable<IChangeLogItem[]> {
    const url = this.projectsUrl + '/GetProjectChangeLog';
    return this.api.post(url, { projectId });
  }

  uploadDocument(formData: FormData): Observable<any> {
    const url = this.documentManagerUrl + '/UploadDocument';
    const additionalParamsMap = new Map<string, any>();
    additionalParamsMap.set('IsFile', true);
    return this.api.post(url, formData, additionalParamsMap);
  }

  deleteDocument(formData: FormData): Observable<IServerResponse> {
    const url = this.documentManagerUrl + '/DeleteDocument';
    const additionalParamsMap = new Map<string, any>();
    additionalParamsMap.set('IsFile', true);
    return this.api.post(url, formData, additionalParamsMap);
  }

  emailProject(emailProject: {
    projectId: number;
    ccEmails: string;
  }): Observable<IServerResponse> {
    const url = this.projectsUrl + '/EmailProject';
    return this.api.post(url, emailProject);
  }

  exportProjects(filter: ISearchProjectFilter): Observable<IFileExport> {
    const url = this.projectsUrl + '/ExportProjects';
    return this.api.post(url, { filter });
  }

  storeColumnView(columns: ColumnState[]): Observable<IServerResponse> {
    const url = this.projectsUrl + '/StoreColumnView';
    return this.api.post(url, columns);
  }

  notifyAllowPublishing(
    projectId: number,
    restrictedWords: string,
    userNote: string,
  ): Observable<IServerResponse> {
    const url = this.projectsUrl + '/NotifyAllowPublishing';
    return this.api.post(url, { projectId, restrictedWords, userNote });
  }
}
